import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import {} from 'react';
import { cn } from '../lib/cn.js';
/**
 * Onboarding scaffold primitives from the v4 prototype. Used by the
 * three onboarding variants (agent-led / form / hybrid).
 *
 * Layout:
 *
 *   ┌───────────────────────────────┐
 *   │  OnbTop (60px, brand+steps)   │
 *   ├──────────────────┬────────────┤
 *   │  OnbPaneLeft     │  OnbPaneRt │
 *   │  (1fr, scrolls)  │  (420px)   │
 *   └──────────────────┴────────────┘
 *
 * Semantic landmarks:
 *   OnbTop      → `<header>`  (page-level brand + step indicator)
 *   OnbPaneLeft → `<main>`    (the primary content area)
 *   OnbPaneRight→ `<aside>`   ("what we've learned" / context rail)
 *
 * The right pane is the "what we've learned" rail in the agent-led
 * variant; in the form variant it shows context / progress; in the
 * hybrid variant it shows agent suggestions.
 */
export function OnbShell({ children, className }) {
    return (_jsx("div", { className: cn('relative grid h-dvh grid-rows-[60px_1fr] overflow-hidden bg-[var(--color-canvas)] text-[var(--color-ink)]', className), children: children }));
}
export function OnbTop({ children, className }) {
    return (_jsx("header", { className: cn('flex items-center justify-between border-b border-[var(--color-line)] px-8', className), children: children }));
}
export function OnbBody({ children, className }) {
    return _jsx("div", { className: cn('grid min-h-0 grid-cols-[1fr_420px]', className), children: children });
}
export function OnbPaneLeft({ children, className }) {
    return (_jsx("main", { className: cn('flex flex-col overflow-y-auto px-16 py-14', className), children: children }));
}
export function OnbPaneRight({ children, className, ariaLabel = 'Onboarding context', }) {
    return (_jsx("aside", { "aria-label": ariaLabel, className: cn('relative overflow-y-auto border-s border-[var(--color-line)] bg-[var(--color-surface)] px-7 py-8', className), children: children }));
}
/**
 * Onboarding step indicator row. Renders an ordered list (`<ol>`) of
 * `<li>` items so assistive tech announces "step 2 of 6" etc. The
 * active step carries `aria-current="step"`.
 *
 * Active / done priority — when a step is both active and done
 * (revisiting a completed step), active wins so the user can still
 * see the "you are here" pointer. The earlier ternary evaluated `done`
 * first and stole the active styling.
 */
export function OnbSteps({ steps, activeIndex, doneThrough, className, }) {
    return (_jsx("ol", { "aria-label": "Onboarding progress", className: cn('mb-9 flex list-none items-center gap-3.5 p-0 font-mono text-2xs uppercase tracking-[0.16em]', className), children: steps.map((label, i) => {
            const isActive = i === activeIndex;
            const isDone = i <= doneThrough;
            return (_jsxs("li", { "aria-current": isActive ? 'step' : undefined, className: "flex items-center gap-2 last:border-none", children: [_jsx("span", { className: cn('inline-grid size-5.5 place-items-center rounded-pill border text-2xs', 
                        // active takes precedence over done — see jsdoc above.
                        isActive
                            ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-accent-ink)]'
                            : isDone
                                ? 'border-[color-mix(in_oklab,var(--color-accent)_40%,transparent)] bg-[var(--color-accent-soft)] text-[var(--color-accent)]'
                                : 'border-[var(--color-line-2)] text-[var(--color-ink-faint)]'), children: i + 1 }), _jsx("span", { className: isActive ? 'text-[var(--color-ink)]' : 'text-[var(--color-ink-faint)]', children: label }), i < steps.length - 1 && (_jsx("span", { "aria-hidden": true, className: "ms-2 h-px w-7 bg-[var(--color-line)]" }))] }, label));
        }) }));
}
